import { 
  getRecurringTransactionsByDateRange, 
  updateRecurringTransaction
} from './db/repositories/recurringTransactions.ts';
import { 
  listTransactions, 
  createTransaction, 
  LocalTransaction 
} from './db/repositories/transactions.ts';

function computeInstallmentAmount(totalAmount: number, installments: number, installmentNumber: number): number {
  if (!installments || installments <= 0 || !installmentNumber || installmentNumber <= 0) return totalAmount;
  const totalCents = Math.round(totalAmount * 100);
  const base = Math.floor(totalCents / installments);
  const remainder = totalCents % installments;
  const amountCents = base + (installmentNumber <= remainder ? 1 : 0);
  return amountCents / 100;
}

/**
 * Gera transações recorrentes para um mês específico (similar ao backend)
 */
export async function generateRecurringTransactionsForMonth(
  userId: number, 
  month: number, 
  year: number
): Promise<LocalTransaction[]> {
  // Calcular range de datas do mês
  const startOfMonth = new Date(year, month - 1, 1);
  const endOfMonth = new Date(year, month, 0, 23, 59, 59);
  
  // Para recorrências sem data fim, usar uma data futura distante
  const farFuture = new Date(year + 10, month - 1, 1).toISOString().split('T')[0];
  const startDateStr = startOfMonth.toISOString().split('T')[0];
  
  // Buscar recorrências ativas que podem gerar transações neste mês
  const recurringTransactions = await getRecurringTransactionsByDateRange(
    startDateStr,
    farFuture
  );
  
  // Filtrar apenas as do usuário atual e ativas
  const userRecurringTransactions = recurringTransactions.filter(rt => rt.user_id === userId && rt.is_active);
  
  const generatedTransactions: LocalTransaction[] = [];
  
  // Buscar transações existentes do mês
  const competence = `${String(month).padStart(2, '0')}/${year}`;
  const existingTransactions = await listTransactions(competence);
  
  for (const recurring of userRecurringTransactions) {
    // Verificar se já existe transação desta recorrência neste mês
    const alreadyExists = existingTransactions.some(existing => {
      // Verificar se tem recurring_transaction_id
      const existingRecurringId = (existing as any).recurring_transaction_id;
      if (existingRecurringId && existingRecurringId === recurring.id) {
        // Para parceladas, verificar também o número da parcela
        if (recurring.recurrence_type === 'installment') {
          const existingInstallment = (existing as any).installment_number;
          // Se não tem installment_number definido, considerar como não existe
          return existingInstallment !== undefined && existingInstallment !== null;
        }
        return true;
      }
      return false;
    });
    
    if (alreadyExists) {
      continue;
    }
    
    // Gerar transação baseado no tipo de recorrência
    let dueDate: Date | null = null;
    let installmentNumber: number | undefined = undefined;
    
    const startDate = new Date(recurring.start_date);
    const startYear = startDate.getFullYear();
    const startMonthNum = startDate.getMonth() + 1;
    const startDay = startDate.getDate();
    
    switch (recurring.recurrence_type) {
      case 'fixed_monthly':
        // Calcular quantos meses desde o início
        const monthsSinceStart = (year - startYear) * 12 + (month - startMonthNum);
        
        // Só gerar se já passou pelo menos 0 meses desde o início (ou está no mês de início)
        if (monthsSinceStart < 0) {
          continue; // Mês ainda não chegou
        }
        
        // Calcular data baseada no dia do mês da data de início
        dueDate = new Date(year, month - 1, startDay);
        
        // Ajustar se o dia não existe no mês (ex: 31 em fevereiro)
        if (dueDate.getMonth() !== month - 1) {
          // Último dia do mês
          dueDate = new Date(year, month, 0);
        }
        break;
        
      case 'fixed_yearly':
        // Mesmo mês e dia da data de início
        if (startMonthNum === month) {
          dueDate = new Date(year, month - 1, startDay);
          if (dueDate.getMonth() !== month - 1) {
            // Último dia do mês
            dueDate = new Date(year, month, 0);
          }
        } else {
          continue; // Não é o mês correto para recorrência anual
        }
        break;
        
      case 'installment':
        // Para parceladas, calcular baseado no número de meses desde o início
        if (!recurring.installments) {
          continue;
        }
        
        const monthsSinceStartInstallment = (year - startYear) * 12 + (month - startMonthNum);
        if (monthsSinceStartInstallment < 0 || monthsSinceStartInstallment >= recurring.installments) {
          continue;
        }
        
        dueDate = new Date(year, month - 1, startDay);
        if (dueDate.getMonth() !== month - 1) {
          // Último dia do mês
          dueDate = new Date(year, month, 0);
        }
        
        installmentNumber = monthsSinceStartInstallment + 1;
        break;
    }
    
    if (!dueDate) {
      continue;
    }
    
    // Verificar se a data está dentro do range válido
    if (dueDate < startOfMonth || dueDate > endOfMonth) {
      continue;
    }
    
    // Verificar se não passou da data de término
    if (recurring.end_date && recurring.recurrence_type !== 'installment') {
      const endDate = new Date(recurring.end_date);
      if (dueDate > endDate) {
        continue;
      }
    }
    
    const amount =
      recurring.recurrence_type === 'installment' &&
      installmentNumber !== undefined &&
      recurring.installments
        ? computeInstallmentAmount(recurring.amount, recurring.installments, installmentNumber)
        : recurring.amount;

    // Criar transação
    const transactionData: Omit<LocalTransaction, 'id' | 'created_at' | 'updated_at' | 'server_id' | 'last_synced_at' | 'needs_sync'> = {
      description: recurring.description,
      amount,
      type: recurring.type,
      account_id: recurring.account_id,
      category_id: recurring.category_id,
      due_date: dueDate.toISOString().split('T')[0],
      is_paid: false,
      // Campos adicionais para recorrência
      ...(installmentNumber !== undefined && { installment_number: installmentNumber } as any),
      ...({ recurring_transaction_id: recurring.id } as any),
      ...({ is_recurring: true } as any)
    };
    
    try {
      const transaction = await createTransaction(transactionData);
      generatedTransactions.push(transaction);
      
      // Atualizar current_installment se for parcelada
      if (recurring.recurrence_type === 'installment' && installmentNumber !== undefined) {
        if (installmentNumber > recurring.current_installment) {
          await updateRecurringTransaction(recurring.id, {
            current_installment: installmentNumber
          });
        }
      }
    } catch (error) {
      console.error('Erro ao criar transação recorrente:', error);
      // Continuar com as próximas
    }
  }
  
  return generatedTransactions;
}

